import { MerchandiseProduct } from '../models/product.model';
import { MerchandiseDto, AddMerchandiseRequest } from '../api/models/merchandise.dto';
import { normalizeImageUrl } from '../utils/image.util';

export class MerchandiseMapper {
  static fromDto(dto: MerchandiseDto): MerchandiseProduct {
    return {
      id: dto.merchandiseId?.toString() || '',
      productId: dto.productId || undefined,
      type: 'merchandise',
      name: dto.merchandiseName,
      description: dto.merchandiseDescription || '',
      otherDetails: dto.otherDetails || '',
      price: dto.price,
      standardCost: dto.standardCost ?? null,
      stockCount: dto.merchandiseCount || 0,
      isActive: dto.isActive !== false,
      status:
        (dto as any).status?.statusName || (dto as any).statusName || (dto as any).status || '-',
      brokerId: dto.brokerId,
      features: (dto.merchandiseFeatures || [])
        .map((f: any) => f.feature?.featureName || '')
        .filter(Boolean),
      featureIds: (dto.merchandiseFeatures || [])
        .map((f: any) => f.feature?.featureId || f.featureId)
        .filter(Boolean),
      merchandisePhotos:
        dto.merchandisePhotos || (dto as any).media || (dto as any).mediaObject || [],
      photoUrl: normalizeImageUrl(this.findThumbnail(dto, 'url')),
      photoThumbnailUrl: normalizeImageUrl(this.findThumbnail(dto, 'thumb')),
      photoMediumUrl: normalizeImageUrl(this.findThumbnail(dto, 'medium')),
      thumbnailPhoto: normalizeImageUrl(
        dto.thumbnailPhoto ||
          (dto as any).merchandiseThumbnailPhoto ||
          this.findThumbnail(dto, 'thumb') ||
          '',
      ),
      media: (dto as any).media || null,
      productLocations: (dto as any).productLocations || [],
      modifiedByStaff: dto.modifiedByStaff,
      modifiedBy: dto.modifiedBy,
      updatedDate: dto.updatedDate,
    } as any;
  }

  static toMerchandiseProduct(dto: MerchandiseDto): MerchandiseProduct {
    return this.fromDto(dto);
  }

  /**
   * Builds the flat payload for POST /api/Merchandise/add-merchandise
   */
  static buildAddRequest(
    existing: MerchandiseDto,
    patch: Partial<MerchandiseProduct>,
    brokerId: number,
    staffId: number,
  ): AddMerchandiseRequest {
    return this.buildEditRequest(existing, patch, brokerId, staffId);
  }

  /**
   * Builds the flat payload for PUT /api/Merchandise/edit-merchandise/{id}
   */
  static buildEditRequest(
    existing: MerchandiseDto,
    patch: Partial<MerchandiseProduct>,
    brokerId: number,
    staffId: number,
  ): AddMerchandiseRequest {
    if (!brokerId || brokerId <= 0) {
      throw new Error(`[MerchandiseMapper] Invalid brokerId: ${brokerId}`);
    }

    return {
      brokerId: brokerId,
      productTypeId: (patch as any).productTypeId ?? 2,
      name: patch.name !== undefined ? (patch.name || '') : (existing.merchandiseName || ''),
      description: patch.description !== undefined ? (patch.description || '') : (existing.merchandiseDescription || ''),
      price: Number(patch.price ?? existing.price ?? 0),
      standardCost: Number(patch.standardCost ?? (existing as any).standardCost ?? 0),
      merchandiseCount: Number(patch.stockCount ?? existing.merchandiseCount ?? 1),
      otherDetails: patch.otherDetails !== undefined ? (patch.otherDetails || '') : (existing.otherDetails || ''),
      featureIds: (patch as any).featureIds || (patch as any).features || [],
      staffId: staffId,
      isActive: patch.isActive ?? existing.isActive ?? true,
    } as any;
  }

  private static findThumbnail(dto: MerchandiseDto, type: 'url' | 'thumb' | 'medium'): string {
    // 1. Check for dedicated thumbnailPhoto object (most recent API structure)
    const tp = (dto as any).merchandiseThumbnailPhoto || dto.thumbnailPhoto;
    if (tp) {
      if (type === 'thumb') return tp.photoThumbnailUrl || tp.photoUrl || '';
      if (type === 'medium') return tp.photoMediumUrl || tp.photoUrl || '';
      return tp.photoUrl || '';
    }

    // 2. Check for media object summary
    const media = (dto as any).media;
    if (media) {
      if (type === 'thumb') return media.photoThumbnailUrl || media.photoUrl || '';
      if (type === 'medium') return media.photoMediumUrl || media.photoUrl || '';
      return media.photoUrl || '';
    }

    // 3. Fallback to photos array
    const photos = dto.merchandisePhotos || [];
    if (photos.length === 0) return '';
    const thumbPhoto = photos.find((p) => p.isThumbnail === true) || photos[0];

    if (thumbPhoto.photo) {
      const p = thumbPhoto.photo;
      if (type === 'thumb') return p.photoThumbnailUrl || p.photoUrl || '';
      if (type === 'medium') return p.photoMediumUrl || p.photoUrl || '';
      return p.photoUrl || '';
    }

    return thumbPhoto.photoUrl || '';
  }
}
